import { faPen, faPlus, faSave, faTimes, faTrash } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Badge } from '@shared/components/ui/badge';
import { Button } from '@shared/components/ui/button';
import type { ConfirmModalRef } from '@shared/components/ui/ConfirmModal';
import { ConfirmModal } from '@shared/components/ui/ConfirmModal';
import { Input } from '@shared/components/ui/input';
import { Product } from '@shared/components/ui/Product';
import { SectionBox } from '@shared/components/ui/SectionBox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@shared/components/ui/shadcn/tabs';
import { StepperInput } from '@shared/components/ui/StepperInput';
import { FC, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  formatCurrencyAmount,
  getCurrencySymbol,
  useCurrency,
  useCurrencySymbol,
} from '../../../shared/hooks/useCurrency';
import { useUpdateOrder } from '../../hooks/useUpdateOrder';
import type { OrderLine, OrderPack, OrderPackaging } from '../../models/Order';
import { PackContentsModal, ProductSelectorModal } from '../modals';
import type { ProductSelectorModalRef } from '../modals/ProductSelectorModal';

interface SelectedItemsProps {
  lines?: OrderLine[];
  packagings?: OrderPackaging[];
  totalShipping?: number;
  totalTax?: number;
  totalDiscount?: number;
  currency?: string;
  orderId?: number;
  statusId?: number;
  onUpdate?: () => void;
}

const PACKAGING_TYPE_LABEL_KEYS: Record<string, string> = {
  box: 'sections.packagings.type.box',
  marketing_item: 'sections.packagings.type.marketing',
  own_box: 'sections.packagings.type.own-box',
  customer_box: 'sections.packagings.type.customer-box',
};

export const SelectedItems: FC<SelectedItemsProps> = ({
  lines = [],
  packagings = [],
  totalShipping = 0,
  totalTax = 0,
  totalDiscount = 0,
  currency,
  orderId,
  statusId,
  onUpdate,
}) => {
  const { t } = useTranslation('viewOrdersModule');
  const productSelectorModalRef = useRef<ProductSelectorModalRef>(null);
  const userCurrencySymbol = useCurrencySymbol();
  const userCurrency = useCurrency();
  const { updateOrder } = useUpdateOrder();
  const canEditOrDelete = statusId === 1 || statusId === 2 || statusId === 3;
  const showPackagingsTab = packagings.length > 0;
  const [activeTab, setActiveTab] = useState('products');
  const showEditActions = canEditOrDelete && (!showPackagingsTab || activeTab === 'products');

  // Selecciona todo el texto para sobrescritura rápida (en algunos navegadores,
  // los inputs type="number" no siempre seleccionan bien solo con onFocus)
  const selectAll = (el: HTMLInputElement) => {
    try {
      el.select();
    } catch {
      // Algunos navegadores restringen selección en type="number"
    }
  };

  const handleSelectAllOnFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    // Esperar a que el navegador complete el focus
    requestAnimationFrame(() => selectAll(e.target));
  };

  const handleSelectAllOnMouseDown = (e: React.MouseEvent<HTMLInputElement>) => {
    // Evitar que el cursor se coloque donde se clickea (al final) y forzar select-all
    e.preventDefault();
    e.currentTarget.focus();
    requestAnimationFrame(() => selectAll(e.currentTarget));
  };

  // Usar la moneda del usuario si no se proporciona una específica
  const displayCurrency = currency ? getCurrencySymbol(currency) : userCurrencySymbol;
  const confirmModalRef = useRef<ConfirmModalRef>(null);

  const handleAddProducts = () => {
    productSelectorModalRef.current?.openModal();
  };

  const handleUpdateProductsFromModal = (
    updatedProducts: any[],
    shippingCosts?: number,
    discounts?: number,
    taxCosts?: number,
  ) => {
    // Convertir los productos del modal a OrderLine y actualizar localLines
    const newLines: OrderLine[] = updatedProducts.map((product, index) => {
      const isProduct = product.type === 'product';
      const isPack = product.type === 'pack';

      return {
        id: Date.now() + index, // ID temporal único para la línea
        product_id: isProduct ? product.id : 0,
        pack_id: isPack ? product.id : null,
        pack_version_id: null,
        name: product.name,
        amount: product.price,
        qty: product.quantity,
        external_product_id: product.external_product_id || '',
        external_variant_id: product.external_variant_id || '',
        product: isProduct
          ? {
              id: product.id,
              name: product.name,
              code: product.code,
              image_hash: product.image || '',
            }
          : (null as any),
        pack: isPack
          ? {
              id: product.id,
              title: product.name,
            }
          : null,
      };
    });

    // Actualizar líneas locales sin enviar a API
    setLocalLines(newLines);

    // Actualizar shipping y discount si se proporcionan
    if (shippingCosts !== undefined) {
      setLocalShipping(shippingCosts);
    }
    if (discounts !== undefined) {
      setLocalDiscount(discounts);
    }
    if (taxCosts !== undefined) {
      setLocalTax(taxCosts);
      setLocalTaxInput(String(taxCosts));
    }

    // Activar modo edición y marcar cambios locales
    setIsEditing(true);
    setHasLocalChanges(true);
  };

  // Estado local para los valores editables de shipping y discounts
  const [localShipping, setLocalShipping] = useState(totalShipping);
  const [localTax, setLocalTax] = useState(totalTax);
  const [localDiscount, setLocalDiscount] = useState(totalDiscount);
  // Estado string para permitir borrar completamente sin que se reinserte "0"
  const [localShippingInput, setLocalShippingInput] = useState<string>(String(totalShipping));
  const [localTaxInput, setLocalTaxInput] = useState<string>(String(totalTax));
  const [localDiscountInput, setLocalDiscountInput] = useState<string>(String(totalDiscount));

  // Estado local para las líneas modificadas (cantidad y precio)
  const [localLines, setLocalLines] = useState<OrderLine[]>(lines);
  const [hasLocalChanges, setHasLocalChanges] = useState(false);
  // Estado string por línea para el importe, permite '' mientras se edita
  const [lineAmountInputs, setLineAmountInputs] = useState<Record<number, string>>({});

  // Estado para controlar el modo de edición
  const [isEditing, setIsEditing] = useState(false);

  // Estado para controlar el loading del botón guardar
  const [isSaving, setIsSaving] = useState(false);

  const [packContentsOpen, setPackContentsOpen] = useState(false);
  const [packContentsTarget, setPackContentsTarget] = useState<OrderPack | null>(null);

  const handlePackContentsOpenChange = (open: boolean) => {
    setPackContentsOpen(open);
    if (!open) setPackContentsTarget(null);
  };

  const openPackContents = (pack: OrderPack) => {
    setPackContentsTarget(pack);
    setPackContentsOpen(true);
  };

  // Actualizar estados locales cuando cambian los props
  useEffect(() => {
    setLocalShipping(totalShipping);
    setLocalTax(totalTax);
    setLocalDiscount(totalDiscount);
    setLocalShippingInput(String(totalShipping));
    setLocalTaxInput(String(totalTax));
    setLocalDiscountInput(String(totalDiscount));
  }, [totalShipping, totalTax, totalDiscount]);

  // Actualizar líneas locales cuando cambian las props
  useEffect(() => {
    setLocalLines(lines);
    setHasLocalChanges(false);
    setIsEditing(false);
    setLineAmountInputs({});
  }, [lines]);

  // Handlers para activar/desactivar modo de edición
  const handleEnableEdit = () => {
    setIsEditing(true);
    // Inicializar inputs string con valores actuales
    setLocalShippingInput(String(localShipping));
    setLocalTaxInput(String(localTax));
    setLocalDiscountInput(String(localDiscount));
    setLineAmountInputs(
      localLines.reduce<Record<number, string>>((acc, line) => {
        acc[line.id] = String(line.amount);
        return acc;
      }, {}),
    );
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setLocalLines(lines);
    setLocalShipping(totalShipping);
    setLocalTax(totalTax);
    setLocalDiscount(totalDiscount);
    setLocalShippingInput(String(totalShipping));
    setLocalTaxInput(String(totalTax));
    setLocalDiscountInput(String(totalDiscount));
    setLineAmountInputs({});
    setHasLocalChanges(false);
  };

  // Handlers locales para cambios de cantidad y precio (sin enviar a API)
  const handleLocalQuantityChange = (id: number, newQuantity: number) => {
    setLocalLines((prevLines) => prevLines.map((line) => (line.id === id ? { ...line, qty: newQuantity } : line)));
    setHasLocalChanges(true);
  };

  const handleLocalPriceChange = (id: number, newPrice: string) => {
    setLineAmountInputs((prev) => ({ ...prev, [id]: newPrice }));

    // Permitir '' mientras se escribe (no forzar a 0 visualmente)
    const trimmed = newPrice.trim();
    const numeric = trimmed === '' ? 0 : parseFloat(trimmed);

    setLocalLines((prevLines) =>
      prevLines.map((line) => (line.id === id ? { ...line, amount: Number.isNaN(numeric) ? 0 : numeric } : line)),
    );
    setHasLocalChanges(true);
  };

  // Handlers locales para shipping y discount (sin enviar a API)
  const handleLocalShippingChange = (newValue: number) => {
    setLocalShipping(newValue);
    setHasLocalChanges(true);
  };

  const handleLocalDiscountChange = (newValue: number) => {
    setLocalDiscount(newValue);
    setHasLocalChanges(true);
  };

  const handleLocalTaxChange = (newValue: number) => {
    setLocalTax(newValue);
    setHasLocalChanges(true);
  };

  const handleLocalShippingInputChange = (newValue: string) => {
    setLocalShippingInput(newValue);
    const trimmed = newValue.trim();
    const numeric = trimmed === '' ? 0 : parseFloat(trimmed);
    handleLocalShippingChange(Number.isNaN(numeric) ? 0 : numeric);
  };

  const handleLocalDiscountInputChange = (newValue: string) => {
    setLocalDiscountInput(newValue);
    const trimmed = newValue.trim();
    const numeric = trimmed === '' ? 0 : parseFloat(trimmed);
    handleLocalDiscountChange(Number.isNaN(numeric) ? 0 : numeric);
  };

  const handleLocalTaxInputChange = (newValue: string) => {
    setLocalTaxInput(newValue);
    const trimmed = newValue.trim();
    const numeric = trimmed === '' ? 0 : parseFloat(trimmed);
    handleLocalTaxChange(Number.isNaN(numeric) ? 0 : numeric);
  };

  // Handler para guardar todos los cambios de productos
  const handleSaveChanges = async () => {
    if (!orderId || !hasLocalChanges || isSaving) return;

    setIsSaving(true);

    try {
      const linesPayload = localLines
        .filter((line) => (line.product && line.product.id && line.product.code) || (line.pack && line.pack.id))
        .map((line) => {
          const isProduct = line.product && line.product.id && line.product.code;

          return {
            ...(isProduct
              ? { product_id: line.product_id, pack_id: undefined }
              : {
                  pack_id: line.pack_id || undefined,
                  product_id: undefined,
                }),
            quantity: line.qty,
            amount: line.amount,
          };
        });

      const payload = {
        lines: linesPayload,
        total_shipping: localShipping,
        total_discount: localDiscount,
        total_tax: localTax,
        currency: currency || userCurrency,
      };

      const success = await updateOrder(orderId, payload);
      if (success) {
        setHasLocalChanges(false);
        setIsEditing(false);
        onUpdate?.();
      } else {
        // Revertir en caso de error
        setLocalLines(lines);
        setHasLocalChanges(false);
      }
    } finally {
      setIsSaving(false);
    }
  };

  // Calcular el subtotal basado en las líneas locales
  const calculatedSubtotal = localLines.reduce((sum, line) => {
    return sum + line.amount * line.qty;
  }, 0);

  // Calcular el total usando el subtotal calculado y los valores locales editables
  const calculatedTotal = calculatedSubtotal + localShipping + localTax - localDiscount;

  const handleDeleteProduct = (productId: number, productName: string) => {
    confirmModalRef.current?.open(
      () => {
        // Actualizar solo la lista local - NO enviar a la API
        // El guardado se hará con el botón "Guardar cambios"
        setLocalLines((prevLines) => prevLines.filter((line) => line.id !== productId));

        // Marcar que hay cambios locales
        setHasLocalChanges(true);
      },
      undefined, // onCancel (opcional)
      t('confirmations.delete-product.title'), // title
      `¿Estás seguro de que quieres eliminar "${productName}" del pedido?`, // message directo
      t('confirmations.delete-product.confirm'), // confirmText
      t('confirmations.delete-product.cancel'), // cancelText
    );
  };

  const productTableHeaders = (
    <div
      className={`hidden md:grid gap-4 text-xs font-medium text-complementary-text border-b border-card-border pb-2 ${
        canEditOrDelete ? 'md:grid-cols-12' : 'md:grid-cols-11'
      }`}
    >
      <div className="col-span-6">{t('sections.selected-items.product')}</div>
      <div className="col-span-3">{t('sections.selected-items.amount')} *</div>
      <div className="col-span-2">{t('sections.selected-items.quantity')} *</div>
      {canEditOrDelete && <div className="col-span-1" />}
    </div>
  );

  const packagingTableHeaders = (
    <div className="hidden md:grid gap-4 text-xs font-medium text-complementary-text border-b border-card-border pb-2 md:grid-cols-11">
      <div className="col-span-6">{t('sections.selected-items.product')}</div>
      <div className="col-span-3">{t('sections.packagings.type-label')}</div>
      <div className="col-span-2">{t('sections.selected-items.quantity')}</div>
    </div>
  );

  const productLines = localLines
    .filter(
      (item) =>
        (item.product && item.product.id && item.product.code) ||
        (item.pack && item.pack.id) ||
        (item.name && !item.product && !item.pack),
    )
    .map((item) => {
      const isProduct = item.product && item.product.id && item.product.code;
      const isPack = item.pack && item.pack.id;
      const isUnlinked = !!item.name && !isProduct && !isPack;

      if (!isProduct && !isPack && !isUnlinked) {
        return null;
      }

      const name = isProduct ? item.product.name : isPack ? item.pack.title : item.name;
      const id = isProduct ? item.product.id : isPack ? item.pack.id : item.id;
      const src = isProduct ? item.product.image_hash : '';
      const code = isProduct
        ? item.product.code
        : isPack
          ? `PACK-${item.pack.id}`
          : t('sections.selected-items.no-code', 'Sin código');
      const showImageModal = !!isProduct;

      return (
        <div key={item.id} className="border-b border-card-border py-2 !mt-0">
          <div
            className={`hidden md:grid gap-4 items-center py-2 ${
              canEditOrDelete ? 'md:grid-cols-12' : 'md:grid-cols-11'
            }`}
          >
            <div className="col-span-6">
              <div className="flex items-center justify-between gap-3 w-full min-w-0">
                <div className="min-w-0 flex-1">
                  <Product
                    id={id}
                    name={name}
                    src={src}
                    code={code}
                    showImageModal={showImageModal}
                    className="max-w-full"
                  />
                </div>
                {!!isPack && item.pack && (
                  <button
                    type="button"
                    className="shrink-0 text-[11px] font-normal text-complementary-text hover:text-foreground/80 transition-colors px-1 py-0.5 -mr-1 bg-transparent border-none cursor-pointer text-right focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background rounded-sm"
                    onClick={() => openPackContents(item.pack)}
                    data-testid={`view-pack-contents-${item.id}`}
                  >
                    {t('sections.selected-items.view-pack-contents')}
                  </button>
                )}
              </div>
            </div>

            <div className="col-span-3">
              {isEditing ? (
                <Input
                  type="number"
                  value={lineAmountInputs[item.id] ?? String(item.amount)}
                  onChange={(e) => handleLocalPriceChange(item.id, e.target.value)}
                  onFocus={handleSelectAllOnFocus}
                  onMouseDown={handleSelectAllOnMouseDown}
                  className="w-full text-left"
                  min="0"
                  step="0.01"
                />
              ) : (
                <span className="text-sm">{formatCurrencyAmount(item.amount, currency || userCurrency)}</span>
              )}
            </div>

            <div className="col-span-2">
              {isEditing ? (
                <StepperInput
                  value={item.qty}
                  onChange={(newVal) => handleLocalQuantityChange(item.id, newVal)}
                  min={1}
                  max={999}
                  className="w-full"
                />
              ) : (
                <span className="text-sm">{item.qty}</span>
              )}
            </div>

            {canEditOrDelete && isEditing && (
              <div className="col-span-1 flex justify-end">
                <button onClick={() => handleDeleteProduct(item.id, name)} className="p-2" title="Eliminar producto">
                  <FontAwesomeIcon icon={faTrash} className="!h-4 !w-4" />
                </button>
              </div>
            )}
          </div>

          <div className="md:hidden space-y-3">
            <div className="flex items-center justify-between gap-3 w-full min-w-0">
              <div className="min-w-0 flex-1">
                <Product
                  id={id}
                  name={name}
                  src={src}
                  code={code}
                  showImageModal={showImageModal}
                  className="max-w-full"
                />
              </div>
              {!!isPack && item.pack && (
                <button
                  type="button"
                  className="shrink-0 text-[11px] font-normal text-complementary-text hover:text-foreground/80 transition-colors px-1 py-0.5 -mr-1 bg-transparent border-none cursor-pointer text-right focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background rounded-sm"
                  onClick={() => openPackContents(item.pack)}
                  data-testid={`view-pack-contents-mobile-${item.id}`}
                >
                  {t('sections.selected-items.view-pack-contents')}
                </button>
              )}
            </div>

            <div className="flex items-center gap-3">
              <div className="flex-1">
                {isEditing ? (
                  <Input
                    type="number"
                    value={lineAmountInputs[item.id] ?? String(item.amount)}
                    onChange={(e) => handleLocalPriceChange(item.id, e.target.value)}
                    onFocus={handleSelectAllOnFocus}
                    onMouseDown={handleSelectAllOnMouseDown}
                    className="w-full text-center"
                    min="0"
                    step="0.01"
                  />
                ) : (
                  <div className="text-center">
                    <span className="text-sm font-medium">
                      {formatCurrencyAmount(item.amount, currency || userCurrency)}
                    </span>
                  </div>
                )}
              </div>

              <div className="flex-1">
                {isEditing ? (
                  <StepperInput
                    value={item.qty}
                    onChange={(newVal) => handleLocalQuantityChange(item.id, newVal)}
                    min={1}
                    max={999}
                    className="w-full"
                  />
                ) : (
                  <div className="text-center">
                    <span className="text-sm font-medium">{item.qty}</span>
                  </div>
                )}
              </div>

              {canEditOrDelete && isEditing && (
                <button onClick={() => handleDeleteProduct(item.id, name)} className="p-2" title="Eliminar producto">
                  <FontAwesomeIcon icon={faTrash} className="!h-4 !w-4" />
                </button>
              )}
            </div>
          </div>
        </div>
      );
    });

  const packagingLines = packagings.map((item) => {
    const typeKey = item.type ? PACKAGING_TYPE_LABEL_KEYS[item.type] : undefined;
    const typeLabel = typeKey ? t(typeKey) : item.type || t('sections.packagings.type.default');

    return (
      <div key={`packaging-${item.id}`} className="border-b border-card-border py-2 !mt-0">
        <div className="hidden md:grid gap-4 items-center py-2 md:grid-cols-11">
          <div className="col-span-6">
            {item.packaging ? (
              <div className="space-y-1">
                <Product
                  id={item.packaging.id}
                  src={item.packaging.image_hash || ''}
                  name={item.packaging.name}
                  code={item.packaging.code}
                  showImageModal
                  className="max-w-full"
                />
                {item.box_group?.name && (
                  <p className="text-xs text-complementary-text pl-12">
                    {t('sections.packagings.group')}: {item.box_group.name}
                  </p>
                )}
              </div>
            ) : (
              <p className="text-sm text-subtext">{t('sections.packagings.unknown')}</p>
            )}
          </div>
          <div className="col-span-3">
            <Badge variant="outline" className="border-0 bg-card-border text-xs">
              {typeLabel}
            </Badge>
          </div>
          <div className="col-span-2">
            <span className="text-sm">{item.qty}</span>
          </div>
        </div>

        <div className="md:hidden space-y-2 py-2">
          {item.packaging ? (
            <Product
              id={item.packaging.id}
              src={item.packaging.image_hash || ''}
              name={item.packaging.name}
              code={item.packaging.code}
              showImageModal
              className="max-w-full"
            />
          ) : (
            <p className="text-sm text-subtext">{t('sections.packagings.unknown')}</p>
          )}
          <div className="flex items-center justify-between gap-2">
            <Badge variant="outline" className="border-0 bg-card-border text-xs">
              {typeLabel}
            </Badge>
            <span className="text-sm font-medium">
              {t('sections.selected-items.quantity')}: {item.qty}
            </span>
          </div>
          {item.box_group?.name && (
            <p className="text-xs text-complementary-text">
              {t('sections.packagings.group')}: {item.box_group.name}
            </p>
          )}
        </div>
      </div>
    );
  });

  return (
    <>
      <SectionBox className="bg-card-bg">
        <div className="flex justify-between items-center">
          <h2 className="text-lg font-bold">{t('sections.selected-items.title')}</h2>
          {showEditActions && (
            <div className="flex gap-2">
              {!isEditing ? (
                /* Botón para activar modo edición */
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleEnableEdit}
                  className="bg-card-bg"
                  data-testid="edit-mode-button"
                >
                  <FontAwesomeIcon icon={faPen} className="!w-3 !h-3" />
                </Button>
              ) : (
                <>
                  {/* Botón para agregar productos nuevos */}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleAddProducts}
                    className="bg-card-bg"
                    data-testid="add-products-button"
                  >
                    <FontAwesomeIcon icon={faPlus} className="!w-3 !h-3" />
                    <span className="hidden sm:inline-block">{t('sections.selected-items.add-products')}</span>
                  </Button>
                  {/* Botón para guardar cambios de cantidad/precio - Siempre visible en modo edición */}
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={handleSaveChanges}
                    data-testid="save-changes-button"
                    disabled={!hasLocalChanges || isSaving}
                    className={isSaving ? 'opacity-50 cursor-wait' : ''}
                  >
                    <FontAwesomeIcon icon={faSave} className="!w-3 !h-3" />
                    <span className="hidden sm:inline-block">{t('sections.selected-items.save-changes')}</span>
                  </Button>
                  {/* Botón para cancelar edición */}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleCancelEdit}
                    className="border-none !pr-0 !pl-1 !bg-transparent !shadow-none"
                    data-testid="cancel-edit-button"
                  >
                    <FontAwesomeIcon icon={faTimes} className="!w-3 !h-3" />
                  </Button>
                </>
              )}
            </div>
          )}
        </div>

        <div className="space-y-4 pt-4">
          {showPackagingsTab ? (
            <Tabs value={activeTab} onValueChange={setActiveTab} defaultValue="products">
              <TabsList className="mb-4">
                <TabsTrigger value="products" data-testid="order-items-tab-products">
                  {t('sections.selected-items.tab-products')}
                </TabsTrigger>
                <TabsTrigger value="packagings" data-testid="order-items-tab-packagings">
                  {t('sections.selected-items.tab-packagings')} ({packagings.length})
                </TabsTrigger>
              </TabsList>

              <TabsContent value="products" className="space-y-4 mt-0">
                {productTableHeaders}
                <div className="space-y-4 !mt-0">{productLines}</div>
              </TabsContent>

              <TabsContent value="packagings" className="space-y-4 mt-0">
                {packagingTableHeaders}
                <div className="space-y-4 !mt-0">{packagingLines}</div>
              </TabsContent>
            </Tabs>
          ) : (
            <>
              {productTableHeaders}
              <div className="space-y-4 !mt-0">{productLines}</div>
            </>
          )}

          {/* Resumen financiero */}
          <div className="space-y-3 pt-2 border-t border-card-border">
            {/* Subtotal Row - Fila de subtotal (suma de todos los productos) */}
            <div className={`flex justify-between items-center text-sm pt-3 ${isEditing ? 'pb-2' : ''}`}>
              <span className="text-complementary-text">{t('sections.selected-items.subtotal')}</span>
              <span>{formatCurrencyAmount(calculatedSubtotal, currency || userCurrency)}</span>
            </div>

            {/* Shipping Costs Row - Fila de costos de envío (editable en modo edición) */}
            <div className="flex justify-between items-center text-sm gap-2">
              <span className="text-complementary-text">{t('sections.selected-items.shipping-costs')}</span>
              {isEditing ? (
                /* Shipping Cost Input - Campo de entrada para costos de envío */
                <div className="flex items-center gap-1">
                  <Input
                    type="number"
                    value={localShippingInput}
                    onChange={(e) => {
                      handleLocalShippingInputChange(e.target.value);
                    }}
                    onFocus={handleSelectAllOnFocus}
                    onMouseDown={handleSelectAllOnMouseDown}
                    className="w-24 text-right"
                    min="0"
                    step="0.01"
                    data-testid="shipping-costs-input"
                  />
                  <span className="text-complementary-text">{displayCurrency}</span>
                </div>
              ) : (
                /* Shipping Cost Display - Mostrar costos de envío (solo lectura) */
                <span>{formatCurrencyAmount(localShipping, currency || userCurrency)}</span>
              )}
            </div>

            {/* Impuestos (total_tax) */}
            <div className="flex justify-between items-center text-sm gap-2">
              <span className="text-complementary-text">{t('sections.selected-items.total-tax')}</span>
              {isEditing ? (
                <div className="flex items-center gap-1">
                  <Input
                    type="number"
                    value={localTaxInput}
                    onChange={(e) => {
                      handleLocalTaxInputChange(e.target.value);
                    }}
                    onFocus={handleSelectAllOnFocus}
                    onMouseDown={handleSelectAllOnMouseDown}
                    className="w-24 text-right"
                    min="0"
                    step="0.01"
                    data-testid="total-tax-input"
                  />
                  <span className="text-complementary-text">{displayCurrency}</span>
                </div>
              ) : (
                <span>{formatCurrencyAmount(localTax, currency || userCurrency)}</span>
              )}
            </div>

            {/* Discounts Row - Fila de descuentos (editable en modo edición) */}
            <div className="flex justify-between items-center text-sm gap-2">
              <span className="text-complementary-text">{t('sections.selected-items.discounts')}</span>
              {isEditing ? (
                /* Discount Input - Campo de entrada para descuentos */
                <div className="flex items-center gap-1">
                  <Input
                    type="number"
                    value={localDiscountInput}
                    onChange={(e) => {
                      handleLocalDiscountInputChange(e.target.value);
                    }}
                    onFocus={handleSelectAllOnFocus}
                    onMouseDown={handleSelectAllOnMouseDown}
                    className="w-24 text-right"
                    min="0"
                    step="0.01"
                    data-testid="discounts-input"
                  />
                  <span className="text-complementary-text">{displayCurrency}</span>
                </div>
              ) : (
                /* Discount Display - Mostrar descuentos (solo lectura) */
                <span>{formatCurrencyAmount(localDiscount, currency || userCurrency)}</span>
              )}
            </div>

            {/* Total Row - Fila de total (subtotal + envío + impuestos - descuentos) */}
            <div className="flex justify-between items-center text-lg font-bold pt-2 border-t border-card-border">
              <span>{t('sections.selected-items.total')}</span>
              <span>{formatCurrencyAmount(calculatedTotal, currency || userCurrency)}</span>
            </div>
          </div>
        </div>
      </SectionBox>

      {/* Modal de selección de productos */}
      <ProductSelectorModal
        ref={productSelectorModalRef}
        onUpdateProducts={handleUpdateProductsFromModal}
        currentOrderProducts={lines
          .filter(
            (line) =>
              // Incluir productos con datos válidos, packs válidos O productos no enlazados
              (line.product && line.product.code) ||
              (line.pack && line.pack.id) ||
              (line.name && !line.product && !line.pack),
          )
          .map((line) => {
            const isProduct = line.product && line.product.code;
            const isPack = line.pack && line.pack.id;

            let type: 'product' | 'pack' | 'unlinked' = 'unlinked';
            if (isProduct) type = 'product';
            if (isPack) type = 'pack';

            return {
              id: isProduct ? line.product_id : isPack ? line.pack_id || 0 : line.id,
              name: isProduct ? line.product.name : isPack ? line.pack.title : line.name || '',
              code: isProduct
                ? line.product.code
                : isPack
                  ? `PACK-${line.pack.id}`
                  : t('sections.selected-items.no-code', 'Sin código'),
              image: isProduct ? line.product.image_hash || '' : '',
              price: line.amount,
              quantity: line.qty,
              isSelected: true,
              type: type,
              external_product_id: line.external_product_id,
              external_variant_id: line.external_variant_id,
            };
          })}
        currentShippingCosts={totalShipping}
        currentDiscounts={totalDiscount}
        currentTax={totalTax}
      />

      {/* Modal de confirmación para eliminar productos */}
      <ConfirmModal ref={confirmModalRef} />

      <PackContentsModal
        open={packContentsOpen}
        onOpenChange={handlePackContentsOpenChange}
        pack={packContentsTarget}
      />
    </>
  );
};
